import { useEffect, useRef, useState } from 'react';

const numberFormatter = new Intl.NumberFormat();

function animationDuration(change) {
  return Math.min(1800, Math.max(650, 500 + Math.log10(change + 1) * 350));
}

export default function ViewsCounter({ count }) {
  const [displayedCount, setDisplayedCount] = useState(count);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPopping, setIsPopping] = useState(false);
  const displayedCountRef = useRef(count);

  useEffect(() => {
    if (typeof count !== 'number') return undefined;

    const startingCount = displayedCountRef.current;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (typeof startingCount !== 'number' || count <= startingCount || prefersReducedMotion) {
      displayedCountRef.current = count;
      setDisplayedCount(count);
      setIsAnimating(false);
      setIsPopping(false);
      return undefined;
    }

    const difference = count - startingCount;
    let popTimeout;

    function showFreshCount() {
      displayedCountRef.current = count;
      setDisplayedCount(count);
      setIsAnimating(false);
      setIsPopping(true);
      popTimeout = window.setTimeout(() => setIsPopping(false), 550);
    }

    // A single integer cannot visibly count up frame-by-frame, so land it
    // immediately and animate the fresh value instead of the cached value.
    if (difference === 1) {
      showFreshCount();
      return () => window.clearTimeout(popTimeout);
    }

    const duration = animationDuration(difference);
    let animationFrame;
    let startedAt;
    setIsPopping(false);
    setIsAnimating(true);

    function animate(timestamp) {
      if (startedAt === undefined) startedAt = timestamp;
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const nextCount = Math.min(count, Math.floor(startingCount + difference * easedProgress));

      displayedCountRef.current = nextCount;
      setDisplayedCount(nextCount);

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(animate);
      } else {
        showFreshCount();
      }
    }

    animationFrame = window.requestAnimationFrame(animate);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(popTimeout);
    };
  }, [count]);

  return (
    <div
      className={`view-counter elevated rounded-xl border border-brand-border bg-brand-surface px-3 py-2 text-xs text-brand-muted ${isAnimating ? 'view-counter--animating' : ''} ${isPopping ? 'view-counter--updated' : ''}`}
      aria-label={`Total Views: ${typeof displayedCount === 'number' ? numberFormatter.format(displayedCount) : 'Unavailable'}`}
    >
      <span>Total Views: </span>
      <span className="view-counter__number inline-block tabular-nums" aria-hidden="true">
        {typeof displayedCount === 'number' ? numberFormatter.format(displayedCount) : 'Unavailable'}
      </span>
    </div>
  );
}
